import { lazy } from 'react';

import { BrowserRouter } from 'react-router';

import { ErrorBoundary } from '@datadog/browser-rum-react';
import { QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider as ReactCoreThemeProvider } from '@cfa/react-core/theme';

import { useInitializeTranslation, useUserAuth } from '@/hooks/app';
import { useColorMode } from '@/hooks/useColorMode';
import {
  useNativeRefreshPathwayCookieListener,
  useNativeThemeChangedListener,
} from '@/hooks/useNativeListener';

import { DatadogRumSetup } from '@/utils/datadog';
import isBrowserSupported from '@/utils/isBrowserSupported';
import { getNameFromLanguage } from '@/utils/language';
import ScrollToTop from '@/utils/ScrollToTop';

import DocumentHead from '@/components/document/DocumentHead';
import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

import Constants from '@/constants';

import { queryClient } from '@/services/api';
import { OktaUserRaw } from '@/types';

import { initializeApp } from './initializeApp';
import LaunchDarklySetup from './LaunchDarklySetup';

import { AmplitudeProvider } from '@/amplitude/AmplitudeContext';
import { useUserInAISearchPOCSetter } from '@/atoms/ai-search-poc';
import { env } from '@/env';
import AuthSecurity from '@/okta/AuthSecurity';
import Layout from '@/routes/Layout/Layout';

const FloatingOpenButton = lazy(
  () => import('@/components/developer-menu/engine/FloatingOpenButton'),
);

if (isBrowserSupported()) {
  initializeApp();
}

const AppContent: React.FC<{
  user?: OktaUserRaw;
  userAuthenticated: boolean;
}> = ({ user, userAuthenticated }) => {
  useNativeRefreshPathwayCookieListener();
  useUserInAISearchPOCSetter();
  // Native → web appearance: listen before useColorMode applies data-color-mode.
  useNativeThemeChangedListener();
  useColorMode();

  return (
    <LaunchDarklySetup>
      <DatadogRumSetup user={user} />
      <AmplitudeProvider user={user}>
        <ReactCoreThemeProvider>
          <LoadingOverlay isOpen={!userAuthenticated} />
          {env.REACT_APP_ENV !== 'prod' && <FloatingOpenButton />}
          <ErrorBoundary fallback={GenericError}>
            <BrowserRouter basename={Constants.BASE_PATH}>
              {/* isNative block is for native mobile view, otherwise web view */}
              {window.native.isNative ? (
                <>
                  <DocumentHead />
                  <ScrollToTop />
                  <Layout />
                </>
              ) : (
                <AuthSecurity>
                  <DocumentHead />
                  <ScrollToTop />
                  <Layout />
                </AuthSecurity>
              )}
            </BrowserRouter>
          </ErrorBoundary>
        </ReactCoreThemeProvider>
      </AmplitudeProvider>
    </LaunchDarklySetup>
  );
};

const App: React.FC = () => {
  const { user, userAuthenticated } = useUserAuth();
  const translationInitialized = useInitializeTranslation();

  if (!translationInitialized) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent user={user} userAuthenticated={userAuthenticated} />
    </QueryClientProvider>
  );
};

if (window.Cypress) {
  window.utils = { getNameFromLanguage };
}

export default App;
